import { ArrowBack, Call } from '@mui/icons-material';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SupportContact from '../components/sos/SupportContact';

/**
 * SOS: Mental health crisis support (UK).
 *
 * WCAG 2.2 AA compliant — semantic headings, visible focus indicators,
 * minimum 48px touch targets, ≥4.5:1 contrast (body) / ≥3:1 (large),
 * tel/sms links, skip-to-content, prefers-reduced-motion respected,
 * keyboard navigable, panic-mode simplification, privacy escape.
 *
 * See PROJECT DOCS/JEFFSAYS_STYLING.md for design tokens.
 */

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

// -- Reusable styles ---------------------------------------------------------

// -- Contrast-safe colour tokens (WCAG 2.2 AA verified) ---------------------
const SOS_RED = '#B00020';
const SOS_RED_HOVER = '#8E001A';
const SOS_BLUE = '#0B4FCC';
const SOS_BLUE_HOVER = '#093DA3';
const TEXT_PRIMARY = '#111111';
const TEXT_SECONDARY = '#333333';
const TEXT_MUTED = '#555555';

const SECTION_HEADING_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: 20,
  color: TEXT_PRIMARY,
  mb: 1.5,
} as const;

const BODY_SX = {
  fontFamily: FONT_FAMILY,
  fontSize: 15,
  lineHeight: 1.6,
  color: TEXT_SECONDARY,
} as const;

// Reason: 3px outlineOffset ensures focus ring is not clipped by overflow
const FOCUS_VISIBLE_SX = {
  '&:focus-visible': {
    outline: '3px solid #000000',
    outlineOffset: '3px',
  },
} as const;

const PRIMARY_BTN_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 700,
  fontSize: 16,
  textTransform: 'none' as const,
  borderRadius: '12px',
  px: 3,
  py: 1.75,
  width: '100%',
  // Reason: WCAG 2.2 Target Size (Minimum) — 48px min
  minHeight: 56,
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  ...FOCUS_VISIBLE_SX,
} as const;

const DROP_SHADOW = 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))';

const CONTACT_LINK_SX = {
  fontFamily: FONT_FAMILY,
  fontWeight: 600,
  fontSize: 15,
  color: SOS_BLUE,
  textDecorationColor: SOS_BLUE,
  // Reason: WCAG 2.2 Target Size (Minimum) — 48px min touch target
  display: 'inline-block',
  minHeight: 48,
  lineHeight: '48px',
  ...FOCUS_VISIBLE_SX,
} as const;

// -- Component ---------------------------------------------------------------

const SOSPage: React.FC = () => {
  const navigate = useNavigate();
  const [simpleMode, setSimpleMode] = useState(false);

  useEffect(() => {
    document.title = 'SOS: Mental health crisis support (UK)';
  }, []);

  const handleBack = (): void => {
    navigate(-1);
  };

  // Reason: privacy escape for users in unsafe/shared environments
  const handleQuickExit = (): void => {
    window.location.replace('https://www.google.com');
  };

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      {/* Skip link — visible on focus for keyboard users */}
      <Link
        href="#sos-support-options"
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: 100,
          fontFamily: FONT_FAMILY,
          fontSize: 14,
          bgcolor: '#FFD400',
          color: '#000000',
          px: 2,
          py: 1,
          borderRadius: '4px',
          textDecoration: 'none',
          '&:focus': {
            position: 'fixed',
            top: 8,
            left: 8,
            width: 'auto',
            height: 'auto',
            overflow: 'visible',
          },
        }}
      >
        Skip to support options
      </Link>

      {/* Header bar */}
      <Box
        component="header"
        sx={{
          bgcolor: SOS_RED,
          width: '100%',
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            aria-label="Go back"
            onClick={handleBack}
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: '32px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              '&:focus-visible': {
                outline: '3px solid #FFFFFF',
                outlineOffset: '3px',
              },
            }}
          >
            <ArrowBack sx={{ color: '#FFFFFF' }} />
          </IconButton>

          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              fontSize: 18,
              color: '#FFFFFF',
            }}
          >
            SOS
          </Typography>
        </Box>

        {/* Privacy escape — discreet exit button */}
        <Button
          onClick={handleQuickExit}
          aria-label="Quickly leave this page"
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            color: '#FFFFFF',
            bgcolor: 'rgba(255,255,255,0.2)',
            borderRadius: '20px',
            px: 1.5,
            py: 0.5,
            minHeight: 36,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            '&:focus-visible': {
              outline: '3px solid #FFFFFF',
              outlineOffset: '3px',
            },
          }}
        >
          Leave page
        </Button>
      </Box>

      {/* Scrollable main content */}
      <Box
        component="main"
        role="main"
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2.5,
          py: 3,
        }}
      >
        {/* Page title — single H1 */}
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            fontSize: simpleMode ? 28 : 24,
            color: TEXT_PRIMARY,
            mb: 1,
          }}
        >
          SOS: Mental health crisis support (UK)
        </Typography>

        {/* Intro */}
        <Typography sx={{ ...BODY_SX, fontSize: simpleMode ? 17 : 15, mb: 0.5 }}>
          If you feel you might harm yourself, or you&rsquo;re worried
          about someone else, use the options below.
        </Typography>
        <Typography
          sx={{
            ...BODY_SX,
            fontWeight: 700,
            fontSize: simpleMode ? 17 : 15,
            color: SOS_RED,
            mb: 2,
          }}
        >
          If there is immediate danger, call 999 now.
        </Typography>

        {/* Panic-mode simplification toggle */}
        <Button
          onClick={() => setSimpleMode((prev: boolean) => !prev)}
          aria-label={simpleMode ? 'Show full page' : 'Make this page simpler'}
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            color: TEXT_MUTED,
            border: '1px solid #D8DADC',
            borderRadius: '8px',
            px: 2,
            py: 0.75,
            mb: 3,
            minHeight: 40,
            ...FOCUS_VISIBLE_SX,
          }}
        >
          {simpleMode ? 'Show full page' : 'Make this page simpler'}
        </Button>

        {/* ── Primary actions ─────────────────────────────── */}
        <Box
          id="sos-support-options"
          component="section"
          aria-labelledby="sos-heading-immediate"
          sx={{ mb: 3 }}
        >
          <Typography
            component="h2"
            id="sos-heading-immediate"
            sx={SECTION_HEADING_SX}
          >
            Immediate danger
          </Typography>

          <Button
            component="a"
            href="tel:999"
            variant="contained"
            startIcon={simpleMode ? undefined : <Call />}
            aria-label="Call 999 for emergency services"
            aria-describedby="sos-desc-999"
            sx={{
              ...PRIMARY_BTN_SX,
              bgcolor: SOS_RED,
              color: '#FFFFFF',
              fontSize: simpleMode ? 18 : 16,
              mb: 1,
              filter: simpleMode ? 'none' : DROP_SHADOW,
              '&:hover': { bgcolor: SOS_RED_HOVER },
            }}
          >
            Call 999 (Emergency)
          </Button>
          {!simpleMode && (
            <Typography
              id="sos-desc-999"
              sx={{ ...BODY_SX, fontSize: 13, color: TEXT_MUTED, mb: 1.5 }}
            >
              Use if you or someone else is in immediate danger, has been
              harmed, or may act on suicidal thoughts right now.
            </Typography>
          )}
          {/* Reason: 12px spacing between primary action buttons */}
          <Box sx={{ mb: 1.5 }} />

          <Typography
            component="h2"
            id="sos-heading-urgent"
            sx={SECTION_HEADING_SX}
          >
            Urgent help
          </Typography>

          <Button
            component="a"
            href="tel:111"
            variant="contained"
            startIcon={simpleMode ? undefined : <Call />}
            aria-label="Call NHS 111 for urgent mental health help"
            aria-describedby="sos-desc-111"
            sx={{
              ...PRIMARY_BTN_SX,
              bgcolor: SOS_BLUE,
              color: '#FFFFFF',
              fontSize: simpleMode ? 18 : 16,
              mb: 1,
              filter: simpleMode ? 'none' : DROP_SHADOW,
              '&:hover': { bgcolor: SOS_BLUE_HOVER },
            }}
          >
            Call NHS 111 (Urgent mental health help)
          </Button>
          {!simpleMode && (
            <Typography
              id="sos-desc-111"
              sx={{ ...BODY_SX, fontSize: 13, color: TEXT_MUTED, mb: 3 }}
            >
              Ask for the mental health option to speak to a trained
              professional and get urgent advice.
            </Typography>
          )}
        </Box>

        {/* ── Steps to get safer ───────────────────────────────── */}
        {!simpleMode && (
          <Box
            component="section"
            aria-labelledby="sos-heading-next"
            sx={{ mb: 3 }}
          >
            <Typography
              component="h2"
              id="sos-heading-next"
              sx={SECTION_HEADING_SX}
            >
              Steps to get safer
            </Typography>

            <Typography sx={{ ...BODY_SX, mb: 1 }}>
              If you can, do one step now:
            </Typography>

            <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 1.5 }}>
              {[
                "Don\u2019t stay alone \u2014 contact someone you trust or stay in a public place.",
                'Move to safety \u2014 go somewhere safer (friend/family, public place, or A&E (Accident & Emergency)).',
                'Create distance from danger \u2014 move away from anything you could use to harm yourself.',
                'Breathe \u2014 in 4 seconds, out 6 seconds (repeat 3 times).',
              ].map((step, i) => (
                <Typography
                  component="li"
                  key={i}
                  sx={{
                    ...BODY_SX,
                    mb: 1,
                    '&::marker': { fontWeight: 700 },
                  }}
                >
                  {step}
                </Typography>
              ))}
            </Box>

            <Typography
              sx={{ ...BODY_SX, fontWeight: 700, color: SOS_RED }}
            >
              If you don&rsquo;t feel able to keep yourself safe right
              now, call 999.
            </Typography>
          </Box>
        )}

        {/* ── Talk or text support ────────────────────────── */}
        <Box
          component="section"
          aria-labelledby="sos-heading-talk"
          sx={{ mb: 3 }}
        >
          <Typography
            component="h2"
            id="sos-heading-talk"
            sx={SECTION_HEADING_SX}
          >
            Talk or text support now (UK-wide)
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Samaritans */}
            <SupportContact
              name="Samaritans"
              availability="24/7"
              actions={[
                { label: 'Call 116 123', href: 'tel:116123', icon: 'call' },
              ]}
            />

            {/* Shout */}
            <SupportContact
              name="Shout"
              availability="24/7 text"
              actions={[
                {
                  label: 'Text SHOUT to 85258',
                  href: 'sms:85258?body=SHOUT',
                  fallbackHref: 'sms:85258',
                  icon: 'sms',
                },
              ]}
            />

            {/* CALM */}
            <SupportContact
              name="CALM"
              availability=""
              actions={[
                {
                  label: 'Call 0800 58 58 58',
                  href: 'tel:08005858558',
                  icon: 'call',
                },
              ]}
            />

            {/* PAPYRUS */}
            <SupportContact
              name="PAPYRUS HOPELINE247"
              availability="for under 35s, or worried about someone under 35"
              actions={[
                {
                  label: 'Call 0800 068 4141',
                  href: 'tel:08000684141',
                  icon: 'call',
                },
                {
                  label: 'Text 88247',
                  href: 'sms:88247',
                  icon: 'sms',
                },
              ]}
            />
          </Box>

          {/* Childline */}
          <Typography
            sx={{
              ...BODY_SX,
              fontSize: 13,
              color: TEXT_MUTED,
              mt: 2,
            }}
          >
            If you&rsquo;re under 19, you can also contact Childline:{' '}
            <Link
              href="tel:08001111"
              sx={CONTACT_LINK_SX}
              aria-label="Call Childline on 0800 1111"
            >
              0800 1111
            </Link>
          </Typography>
        </Box>

        {/* ── Helping someone else ────────────────────────── */}
        {!simpleMode && (
          <Box
            component="section"
            aria-labelledby="sos-heading-helping"
            sx={{ mb: 3 }}
          >
            <Typography
              component="h2"
              id="sos-heading-helping"
              sx={SECTION_HEADING_SX}
            >
              If you&rsquo;re helping someone else
            </Typography>

            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
              <Typography component="li" sx={{ ...BODY_SX, mb: 1 }}>
                <strong>If they&rsquo;re in immediate danger:</strong>{' '}
                call 999.
              </Typography>
              <Typography component="li" sx={BODY_SX}>
                <strong>If not:</strong> stay with them if you can, and
                call{' '}
                <Link
                  href="tel:111"
                  sx={CONTACT_LINK_SX}
                  aria-label="Call NHS 111 for urgent mental health help"
                >
                  NHS 111
                </Link>{' '}
                (mental health option) or{' '}
                <Link
                  href="tel:116123"
                  sx={CONTACT_LINK_SX}
                  aria-label="Call Samaritans on 116 123"
                >
                  Samaritans
                </Link>{' '}
                for support and guidance.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Fallback note for text links */}
        {!simpleMode && (
          <Typography
            sx={{
              ...BODY_SX,
              fontSize: 12,
              // Reason: #767676 is lightest colour passing 4.5:1 on white
              color: '#767676',
              textAlign: 'center',
              mt: 2,
              mb: 4,
            }}
          >
            If a text link doesn&rsquo;t open your messaging app, text
            the keyword to the number shown.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SOSPage;
